import { logRuntimeError } from 'Diagnostics/diagnostics';
import type { SearchItemRawData } from 'Models/searchItemData';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import Vehicle, { type VehicleData, type VehicleExtra, type VehicleValue } from 'Models/vehicle';
import VehicleCollection from 'Models/vehicleCollection';

import {
  getCookie,
  isStorageAllowed,
  localStorage,
  removeCookie,
  sessionStorage,
  setCookie,
} from './browserStorage';
import { isBoolean, isString } from './guards';
import keys from './userStorageKeys';

type ProductHistoryItem = SearchItemRawData & { url?: string };
type TrackedThemeId = string | number;
type VehicleRawData = VehicleData;
type StorageValue = unknown;

const productSize = 3;
export const loadProductsHistory = (): ProductHistoryItem[] => {
  const serializedState = getItem(keys.productsHistory, isProductHistoryItemArray);
  if (!serializedState) {
    return [];
  }
  const url = location.href;
  return serializedState.filter((record) => record.url !== url);
};
export const addProductsHistory = (product: ProductHistoryItem): void => {
  const productsHistory = loadProductsHistory();
  if (productsHistory.length >= productSize) {
    productsHistory.pop();
  }
  const url = location.href;
  productsHistory.unshift({ ...product, url });
  setItem(keys.productsHistory, productsHistory);
};
function isProductHistoryItemArray(value: unknown): value is ProductHistoryItem[] {
  return (
    Array.isArray(value) &&
    value.every((v) => {
      if (v === null || typeof v !== 'object' || Array.isArray(v)) {
        return false;
      }
      const { url } = v as { url?: unknown };
      return url === undefined || typeof url === 'string';
    })
  );
}

export function loadSelectedVehicle(): typeof Vehicle.null | Vehicle {
  const { doNotLoadSelectedVehicle } = fitmentSearchConfig;
  if (doNotLoadSelectedVehicle) {
    return Vehicle.null;
  }

  const data = getVehicleData(keys.vehicle);
  return isVehicleRawData(data) ? deserializeVehicle(data) : Vehicle.null;
}
function getVehicleData(key: string): unknown {
  const expires =
    fitmentSearchConfig.vehicleDataExpires && !fitmentSearchConfig.disableVehicleStorage()
      ? new Date(Date.now() + fitmentSearchConfig.vehicleDataExpires * 60 * 1000)
      : null;

  return getItemRaw(key, !!fitmentSearchConfig.vehicleDataExpires, expires);
}
export function setVehicleData(key: string, value: StorageValue): void {
  if (!fitmentSearchConfig.disableVehicleStorage()) {
    const expires = fitmentSearchConfig.vehicleDataExpires
      ? new Date(Date.now() + fitmentSearchConfig.vehicleDataExpires * 60 * 1000)
      : undefined;

    setItem(key, value, !!fitmentSearchConfig.vehicleDataExpires, expires);
  }
}
export function removeVehicleData(key: string): void {
  removeItem(key, !!fitmentSearchConfig.vehicleDataExpires);
}
export function loadGarageData(): VehicleCollection {
  const data = getVehicleData(keys.garageData);
  return Array.isArray(data) && data.length
    ? new VehicleCollection(data.filter(isVehicleRawData).map(deserializeVehicle))
    : VehicleCollection.empty;
}
export function getForceVehicleSelectionLater(): boolean | null {
  return getItem(keys.forceVehicleSelectionLater, isBoolean, true);
}
export function setForceVehicleSelectionLater(): void {
  const expires = new Date(Date.now() + fitmentSearchConfig.forceVehicleSelectionInterval * 60 * 1000);
  setItem(keys.forceVehicleSelectionLater, true, true, expires);
}
function isVehicleRawData(value: unknown): value is VehicleRawData {
  return Array.isArray(value) && value.every((entry) => entry !== null && typeof entry === 'object');
}
function deserializeVehicle(data: VehicleRawData): Vehicle {
  const vehicleExtraData =
    data.find(
      (entry): entry is { vehicleExtra: VehicleExtra } => 'vehicleExtra' in entry && !!entry.vehicleExtra,
    )?.vehicleExtra ?? {};
  const vehicleValues = data.filter((entry): entry is VehicleValue => !('vehicleExtra' in entry));

  return new Vehicle(vehicleValues, fitmentSearchConfig.fields, vehicleExtraData);
}

function isTrackedThemeId(value: unknown): value is TrackedThemeId {
  return typeof value === 'string' || typeof value === 'number';
}
export function getFacetToggleState(field: string): boolean | null {
  const value = getItem(
    keys.facetToggleField(field),
    (value): value is boolean | 'true' | 'false' =>
      typeof value === 'boolean' || value === 'true' || value === 'false',
  );
  return value === null ? null : value === true || value === 'true';
}

export function getTrackedTheme(): TrackedThemeId | null {
  return getItem(keys.trackedThemeId, isTrackedThemeId);
}
export function setTrackedTheme(id: TrackedThemeId): void {
  setItem(keys.trackedThemeId, id);
}

export function isThemeTelemetryEnabled(rate: number): boolean {
  if (rate >= 1) {
    return true;
  }
  if (rate <= 0) {
    return false;
  }

  const stored = sessionStorage.getItem(keys.themeTelemetryEnabled);
  if (stored !== null) {
    return stored === '1';
  }

  const enabled = Math.random() < rate;
  sessionStorage.setItem(keys.themeTelemetryEnabled, enabled ? '1' : '0');
  return enabled;
}

export function isLoggedInStateChanged(isLoggedInNow: boolean): boolean {
  const wasLoggedIn = getItem(keys.isLoggedIn, isBoolean);
  setItem(keys.isLoggedIn, isLoggedInNow);
  return wasLoggedIn !== isLoggedInNow;
}

export function loadView(): string | null {
  return getItem(keys.view, isString);
}

export function getPartsAdvisorOpen(): boolean {
  return sessionStorage.getItem(keys.partsAdvisorOpen) === '1';
}
export function setPartsAdvisorOpen(open: boolean): void {
  sessionStorage.setItem(keys.partsAdvisorOpen, open ? '1' : '0');
}

export function getSearchId(): string | null {
  return getItem(keys.searchId, isString);
}
export function setSearchId(searchId: string): void {
  setItem(keys.searchId, searchId);
}

function safeJsonParse(text: string, source: string): unknown {
  try {
    return JSON.parse(text);
  } catch (error) {
    logRuntimeError({
      source: `userStorage:${source}`,
      error,
      extra: { 'value.preview': text.length > 200 ? `${text.slice(0, 200)}…` : text },
    });
    return null;
  }
}
function getItemRaw(key: string, useCookie?: boolean, expires?: Date | null): unknown {
  if (useCookie) {
    const value = getCookie(key);
    if (expires && value) {
      setCookie(key, value, expires);
    }
    if (!value) {
      return null;
    }
    return safeJsonParse(value.replace(/\+/g, ' '), `cookie:${key}`);
  }
  const value = localStorage.getItem(key);
  return value ? safeJsonParse(value, `localStorage:${key}`) : null;
}
export function getItem<T>(
  key: string,
  predicate: (value: unknown) => value is T,
  useCookie?: boolean,
  expires?: Date | null,
): T | null {
  const raw = getItemRaw(key, useCookie, expires);
  if (raw === null) {
    return null;
  }
  if (predicate(raw)) {
    return raw;
  }
  logRuntimeError({
    source: `userStorage:get:${key}`,
    error: new Error(`storage value for key "${key}" did not match expected shape`),
  });
  return null;
}

function safeStringify(value: StorageValue, source: string): string | null {
  try {
    const result = JSON.stringify(value);
    if (result === undefined) {
      logRuntimeError({
        source: `userStorage:set:${source}`,
        error: new Error('value is not JSON-serializable (function, symbol, or undefined root)'),
      });
      return null;
    }
    return result;
  } catch (error) {
    logRuntimeError({
      source: `userStorage:set:${source}`,
      error,
    });
    return null;
  }
}
export function setItem(
  key: string,
  value: StorageValue,
  useCookie?: boolean,
  expires: Date | undefined = new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000),
): void {
  if (!isStorageAllowed(key)) {
    return;
  }
  if (useCookie) {
    setCookie(key, value, expires);
  } else {
    const serialized = safeStringify(value, key);
    if (serialized === null) {
      return;
    }
    localStorage.setItem(key, serialized);
  }
}

export function removeItem(key: string, useCookie?: boolean): void {
  if (useCookie) {
    removeCookie(key);
  } else {
    localStorage.removeItem(key);
  }
}
